/**
 * Lets the Sentry user-feedback widget coexist with open Radix dialogs, so a modal
 * can stay open (and be screenshotted) while the feedback form is in use.
 *
 * The widget renders its form inside a shadow DOM whose host (`#sentry-feedback`)
 * is appended directly to `<body>` — outside any Radix dialog. A modal Radix dialog
 * sets `body { pointer-events: none }` and traps focus, which breaks the widget in
 * three ways:
 *
 *   1. The host inherits `pointer-events: none`, so clicks on the feedback form pass
 *      straight *through* it to the dimmed overlay behind, which Radix's
 *      DismissableLayer reads as an outside click and dismisses the dialog. Forcing
 *      `pointer-events: auto` on the host makes the form capture its own clicks.
 *   2. Captured pointer/focus events still bubble (retargeted to the host in the
 *      light DOM) up to `document`, where DismissableLayer (pointerdown/click/
 *      focusin) would dismiss the dialog. Bubble-phase listeners on the host that
 *      call `stopPropagation()` hide them from `document` while leaving the widget's
 *      own (deeper) shadow handlers untouched.
 *   3. When focus moves into the widget, the `focusout` fires on the element losing
 *      focus — which lives inside the *modal*, not the widget — so it never reaches
 *      the host listeners above. The modal's FocusScope sees that focusout, finds
 *      the new focus target (the host) outside its container, and yanks focus back.
 *      A capture-phase `focusout` listener on `document` (ahead of FocusScope's own)
 *      swallows it whenever focus is entering the widget.
 *
 * The host is created lazily the first time the widget opens, so we watch `<body>`
 * for it. Call once after `Sentry.init()`; returns a cleanup function.
 */
const SENTRY_FEEDBACK_HOST_ID = 'sentry-feedback';

// Events that originate inside the widget and bubble (retargeted to the host) up to
// `document`, where Radix's DismissableLayer would dismiss the dialog or steal focus.
const HOST_BUBBLE_EVENTS = ['pointerdown', 'click', 'focusin'] as const;

export const guardSentryFeedbackFromDialogDismiss = (): (() => void) => {
  if (typeof document === 'undefined') {
    return () => undefined;
  }

  let guardedHost: HTMLElement | undefined;

  const stopPropagation = (event: Event): void => event.stopPropagation();

  // Swallow the modal-side `focusout` that fires when focus enters (or moves within)
  // the widget, so the modal's FocusScope can't pull focus back out of the form.
  const stopFocusStealIntoWidget = (event: FocusEvent): void => {
    const host = guardedHost;
    const relatedTarget = event.relatedTarget;
    if (host && relatedTarget instanceof Node && (relatedTarget === host || host.contains(relatedTarget))) {
      event.stopImmediatePropagation();
    }
  };

  const attachTo = (host: HTMLElement): void => {
    if (guardedHost === host) {
      return;
    }
    guardedHost = host;
    // Re-enable interaction even when an open modal disables pointer events on the body.
    host.style.pointerEvents = 'auto';
    for (const eventName of HOST_BUBBLE_EVENTS) {
      host.addEventListener(eventName, stopPropagation);
    }
  };

  const findAndAttach = (): void => {
    const host = document.getElementById(SENTRY_FEEDBACK_HOST_ID);
    if (host) {
      attachTo(host);
    }
  };

  document.addEventListener('focusout', stopFocusStealIntoWidget, true);
  findAndAttach();

  const observer = new MutationObserver(findAndAttach);
  observer.observe(document.body, { childList: true });

  return () => {
    observer.disconnect();
    document.removeEventListener('focusout', stopFocusStealIntoWidget, true);
    if (guardedHost) {
      for (const eventName of HOST_BUBBLE_EVENTS) {
        guardedHost.removeEventListener(eventName, stopPropagation);
      }
      guardedHost.style.pointerEvents = '';
      guardedHost = undefined;
    }
  };
};
